import React from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { SavedViewTabPill } from "./SavedViewTabPill";
import { ManageViewsDialog } from "./ManageViewsDialog";
import type { FieldMap } from "../../../UniversalCRMTable/components/SimpleTable/filterDisplayUtils";
import type { SavedViewConfig } from "../../../UniversalCRMTable/types";
import type { TabItem } from "./types";

interface SavedViewsTabsUIProps {
  tabs: TabItem[];
  views: SavedViewConfig[];
  fields: FieldMap;
  activeViewId: string | null;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onScroll: (direction: "left" | "right") => void;
  onTabClick: (viewId: string | null) => void;
  isUserCreatedView: (viewId: string) => boolean;
  isFavorite: (viewId: string) => boolean;
  onToggleFavorite: (viewId: string) => void;
  onShare: (viewId: string) => void;
  onDelete: (viewId: string) => void;
  onAddView: () => void;
  showAddButton: boolean;
  isViewHidden: (viewId: string) => boolean;
  onHideView: (viewId: string) => void;
  onUnhideView: (viewId: string) => void;
  /** Canonical full order of view ids (Main excluded) for the Manage Views dialog. */
  orderedViewIds: string[];
  /** Reorder the *visible* pill subset (drag on the bar). */
  onReorderVisible: (visibleViewIds: string[]) => void;
  /** Reorder the full view list (drag in the Manage Views dialog). */
  onReorderAll: (viewIds: string[]) => void;
}

/**
 * One draggable saved-view pill. "Main" is rendered separately and is never
 * sortable, so every id handed to the SortableContext is a real view id.
 *
 * The PointerSensor uses an 8px activation distance, so a plain click still
 * selects the view (and the pill's inner buttons/menus keep working) — only a
 * deliberate drag starts a reorder.
 */
const SortableTabPill: React.FC<{
  tab: TabItem & { id: string };
  view?: SavedViewConfig;
  fields: FieldMap;
  isActive: boolean;
  isUserCreated: boolean;
  isFavorite: boolean;
  onTabClick: (viewId: string | null) => void;
  onToggleFavorite: (viewId: string) => void;
  onShare: (viewId: string) => void;
  onDelete: (viewId: string) => void;
  onHide: (viewId: string) => void;
}> = ({ tab, ...pillProps }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tab.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // While dragging, keep this pill above its neighbours.
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <SavedViewTabPill tab={tab} {...pillProps} />
    </div>
  );
};

export const SavedViewsTabsUI: React.FC<SavedViewsTabsUIProps> = ({
  tabs,
  views,
  fields,
  activeViewId,
  canScrollLeft,
  canScrollRight,
  scrollContainerRef,
  onScroll,
  onTabClick,
  isUserCreatedView,
  isFavorite,
  onToggleFavorite,
  onShare,
  onDelete,
  onAddView,
  showAddButton,
  isViewHidden,
  onHideView,
  onUnhideView,
  orderedViewIds,
  onReorderVisible,
  onReorderAll,
}) => {
  const viewsById = React.useMemo(() => {
    const map = new Map<string, SavedViewConfig>();
    views.forEach((view) => map.set(view.id, view));
    return map;
  }, [views]);

  const mainTab = tabs.find((t) => t.id === null);
  const sortableTabs = React.useMemo(
    () => tabs.filter((t): t is TabItem & { id: string } => t.id !== null),
    [tabs]
  );
  const sortableIds = React.useMemo(
    () => sortableTabs.map((t) => t.id),
    [sortableTabs]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sortableIds.indexOf(String(active.id));
      const newIndex = sortableIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;
      onReorderVisible(arrayMove(sortableIds, oldIndex, newIndex));
    },
    [sortableIds, onReorderVisible]
  );

  const renderPill = (tab: TabItem, sortable: boolean): React.ReactNode => {
    const pillProps = {
      view: tab.id ? viewsById.get(tab.id) : undefined,
      fields,
      isActive: activeViewId === tab.id,
      isUserCreated: !!(tab.id && isUserCreatedView(tab.id)),
      isFavorite: !!(tab.id && isFavorite(tab.id)),
      onTabClick,
      onToggleFavorite,
      onShare,
      onDelete,
      onHide: onHideView,
    };
    if (sortable && tab.id) {
      return (
        <SortableTabPill
          key={tab.id}
          tab={tab as TabItem & { id: string }}
          {...pillProps}
        />
      );
    }
    return <SavedViewTabPill key={tab.id || "main"} tab={tab} {...pillProps} />;
  };

  return (
    <div className="mb-2 px-4 md:px-0">
      <div className="flex items-center gap-0.5">
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onScroll("left")}
            className="h-5 w-5 p-0 flex-shrink-0 hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label="Scroll tabs left" title="Scroll tabs left"
          >
            <ChevronLeft className="h-2.5 w-2.5" />
          </Button>
        )}

        {/* Tabs Container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-0.5 overflow-x-auto scrollbar-hide flex-1"
          style={{ scrollBehavior: "smooth" }}
        >
          {/* Main is pinned first and never draggable. */}
          {mainTab && renderPill(mainTab, false)}

          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableIds}
              strategy={horizontalListSortingStrategy}
            >
              {sortableTabs.map((tab) => renderPill(tab, true))}
            </SortableContext>
          </DndContext>

          {/* Add Button */}
          {showAddButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onAddView}
              className="h-6 w-6 p-0 flex-shrink-0 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              title="Create new saved view"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* Right Scroll Button */}
        {canScrollRight && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onScroll("right")}
            className="h-5 w-5 p-0 flex-shrink-0 hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label="Scroll tabs right" title="Scroll tabs right"
          >
            <ChevronRight className="h-2.5 w-2.5" />
          </Button>
        )}

        {/* Reorder + show/hide views preference dialog (localStorage-backed) */}
        <ManageViewsDialog
          views={views}
          orderedIds={orderedViewIds}
          onReorder={onReorderAll}
          isHidden={isViewHidden}
          onHide={onHideView}
          onUnhide={onUnhideView}
          activeViewId={activeViewId}
        />
      </div>
    </div>
  );
};
